import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

type ThreeModule = typeof import("three");

export interface SurfaceCorrelogramColumn {
  key: string;
  header: string;
  unit?: string;
}

export type SurfaceViewState = {
  yaw: number;
  pitch: number;
  zoom: number;
  panX: number;
  panY: number;
};

export type SurfaceSamplePoint = {
  x: number;
  y: number;
  value: number;
  weight: number;
  rowIndex: number;
};

export type SurfaceGrid = {
  cells: Float32Array;
  counts: Uint32Array;
  cols: number;
  rows: number;
  min: number;
  max: number;
  points: SurfaceSamplePoint[];
  sampleCount: number;
  observedCells: number;
  directPairs: number;
  pearsonR: number;
};

interface NumericSeries {
  key: string;
  label: string;
  values: Float32Array;
  valid: Uint8Array;
  min: number;
  max: number;
  span: number;
  count: number;
}

type SurfacePanel = {
  key: string;
  grid: SurfaceGrid;
  xLabel: string;
  yLabel: string;
  zLabel: string;
};

type ThreePanelHandle = SurfacePanel & {
  scene: InstanceType<ThreeModule["Scene"]>;
  camera: InstanceType<ThreeModule["PerspectiveCamera"]>;
  group: InstanceType<ThreeModule["Group"]>;
};

type ThreeRuntime = {
  renderer: InstanceType<ThreeModule["WebGLRenderer"]>;
  handles: ThreePanelHandle[];
  observer: ResizeObserver;
  render: () => void;
};

export interface SurfaceCorrelogramProps {
  rows: Record<string, unknown>[];
  numericColumns: SurfaceCorrelogramColumn[];
  zAxisKey?: string;
  zAxisLabel?: string;
  zValueResolver?: (context: {
    row: Record<string, unknown>;
    rowIndex: number;
    xKey: string;
    yKey: string;
    zKey: string;
  }) => number | null | undefined;
  selectedRowIdx?: number;
  onPointClick?: (rowIdx: number) => void;
  maxColumns?: number;
  gridResolution?: number;
  pointSampleLimit?: number;
}

const LABEL_COL_W = 72;
const HEADER_ROW_H = 22;
const DEFAULT_VIEW: SurfaceViewState = { yaw: -0.52, pitch: 0.74, zoom: 1, panX: 0, panY: 0 };
const LAYOUT = { width: 3.1, depth: 1.24, lift: 1.1 };

function numericValue(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) return Number(value);
  return Number.NaN;
}

function formatSmall(value: number): string {
  if (!Number.isFinite(value)) return "n/a";
  if (Math.abs(value) >= 10000 || (Math.abs(value) < 0.01 && value !== 0)) return value.toExponential(1);
  if (Math.abs(value) >= 100) return value.toFixed(1);
  if (Math.abs(value) >= 10) return value.toFixed(2);
  return value.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
}

function clampNumber(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function normalise(value: number, series: NumericSeries): number {
  if (!Number.isFinite(value)) return Number.NaN;
  if (series.span <= 1e-12) return 0.5;
  return clampNumber((value - series.min) / series.span, 0, 1);
}

function valueColor(t: number): string {
  const stops: Array<[number, [number, number, number]]> = [
    [0, [37, 80, 154]],
    [0.24, [31, 130, 178]],
    [0.5, [26, 168, 132]],
    [0.74, [188, 201, 73]],
    [1, [226, 118, 43]],
  ];
  const clamped = clampNumber(t, 0, 1);
  for (let idx = 0; idx < stops.length - 1; idx += 1) {
    const [a, ca] = stops[idx]!;
    const [b, cb] = stops[idx + 1]!;
    if (clamped <= b) {
      const local = (clamped - a) / Math.max(1e-12, b - a);
      const r = Math.round(ca[0] + (cb[0] - ca[0]) * local);
      const g = Math.round(ca[1] + (cb[1] - ca[1]) * local);
      const bValue = Math.round(ca[2] + (cb[2] - ca[2]) * local);
      return `rgb(${r},${g},${bValue})`;
    }
  }
  return "rgb(226,118,43)";
}

function buildSeries(rows: Record<string, unknown>[], columns: SurfaceCorrelogramColumn[]): Map<string, NumericSeries> {
  const result = new Map<string, NumericSeries>();
  for (const column of columns) {
    const values = new Float32Array(rows.length);
    const valid = new Uint8Array(rows.length);
    let min = Infinity;
    let max = -Infinity;
    let count = 0;
    for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
      const value = numericValue(rows[rowIndex]?.[column.key]);
      if (!Number.isFinite(value)) {
        values[rowIndex] = Number.NaN;
        continue;
      }
      values[rowIndex] = value;
      valid[rowIndex] = 1;
      count += 1;
      if (value < min) min = value;
      if (value > max) max = value;
    }
    if (!count) {
      min = 0;
      max = 1;
    }
    result.set(column.key, {
      key: column.key,
      label: column.header,
      values,
      valid,
      min,
      max,
      span: Math.max(1e-12, max - min),
      count,
    });
  }
  return result;
}

function hash32(value: number): number {
  let next = value | 0;
  next = Math.imul(next ^ (next >>> 16), 0x7feb352d);
  next = Math.imul(next ^ (next >>> 15), 0x846ca68b);
  return (next ^ (next >>> 16)) >>> 0;
}

function fillSurfaceGaps(cells: Float32Array, counts: Uint32Array, cols: number, rows: number, fallback: number): void {
  const populated: Array<{ col: number; row: number; value: number }> = [];
  for (let idx = 0; idx < cells.length; idx += 1) {
    if (counts[idx]! > 0 && Number.isFinite(cells[idx]!)) {
      populated.push({ col: idx % cols, row: Math.floor(idx / cols), value: cells[idx]! });
    }
  }
  if (!populated.length) {
    cells.fill(fallback);
    return;
  }
  for (let idx = 0; idx < cells.length; idx += 1) {
    if (counts[idx]! > 0 && Number.isFinite(cells[idx]!)) continue;
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    let valueSum = 0;
    let weightSum = 0;
    for (const item of populated) {
      const distance2 = (item.col - col) ** 2 + (item.row - row) ** 2;
      const weight = 1 / Math.max(1e-4, distance2 ** 1.35);
      valueSum += item.value * weight;
      weightSum += weight;
    }
    cells[idx] = weightSum > 0 ? valueSum / weightSum : fallback;
  }
  const previous = cells.slice();
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const idx = row * cols + col;
      if (counts[idx]! > 0) continue;
      let valueSum = 0;
      let weightSum = 0;
      for (let dy = -1; dy <= 1; dy += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          const xx = clampNumber(col + dx, 0, cols - 1);
          const yy = clampNumber(row + dy, 0, rows - 1);
          const weight = dx === 0 && dy === 0 ? 2 : 1;
          valueSum += previous[yy * cols + xx]! * weight;
          weightSum += weight;
        }
      }
      cells[idx] = valueSum / Math.max(1, weightSum);
    }
  }
}

function buildSurfaceGrid(
  xSeries: NumericSeries,
  ySeries: NumericSeries,
  zSeries: NumericSeries,
  rows: Record<string, unknown>[],
  zValueResolver: SurfaceCorrelogramProps["zValueResolver"] | undefined,
  gridResolution: number,
  pointSampleLimit: number,
): SurfaceGrid {
  const cols = gridResolution;
  const gridRows = gridResolution;
  const cellCount = cols * gridRows;
  const sums = new Float64Array(cellCount);
  const counts = new Uint32Array(cellCount);
  const points: SurfaceSamplePoint[] = [];
  let directPairs = 0;
  let sampleCount = 0;
  let sumZ = 0;
  let sx = 0;
  let sy = 0;
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  const total = Math.min(xSeries.values.length, ySeries.values.length, zSeries.values.length, rows.length);
  for (let rowIndex = 0; rowIndex < total; rowIndex += 1) {
    if (!xSeries.valid[rowIndex] || !ySeries.valid[rowIndex]) continue;
    const xRaw = xSeries.values[rowIndex]!;
    const yRaw = ySeries.values[rowIndex]!;
    directPairs += 1;
    sx += xRaw;
    sy += yRaw;
    sxx += xRaw * xRaw;
    syy += yRaw * yRaw;
    sxy += xRaw * yRaw;
    const resolved = zValueResolver
      ? zValueResolver({
          row: rows[rowIndex]!,
          rowIndex,
          xKey: xSeries.key,
          yKey: ySeries.key,
          zKey: zSeries.key,
        })
      : zSeries.valid[rowIndex]
        ? zSeries.values[rowIndex]!
        : Number.NaN;
    const zRaw = numericValue(resolved);
    const x = normalise(xRaw, xSeries);
    const y = normalise(yRaw, ySeries);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(zRaw)) continue;
    const col = Math.trunc(clampNumber(Math.floor(x * cols), 0, cols - 1));
    const row = Math.trunc(clampNumber(Math.floor(y * gridRows), 0, gridRows - 1));
    const idx = row * cols + col;
    sums[idx] = (sums[idx] ?? 0) + zRaw;
    counts[idx] = (counts[idx] ?? 0) + 1;
    sampleCount += 1;
    sumZ += zRaw;
    const point = { x, y, value: zRaw, weight: 1, rowIndex };
    if (points.length < pointSampleLimit) {
      points.push(point);
    } else {
      const slot = hash32(rowIndex + sampleCount * 17) % sampleCount;
      if (slot < pointSampleLimit) points[slot] = point;
    }
  }
  const fallback = sampleCount > 0 ? sumZ / sampleCount : 0;
  const cells = new Float32Array(cellCount);
  let observedCells = 0;
  for (let idx = 0; idx < cellCount; idx += 1) {
    if (counts[idx]! > 0) {
      cells[idx] = (sums[idx] ?? 0) / counts[idx]!;
      observedCells += 1;
    } else {
      cells[idx] = fallback;
    }
  }
  fillSurfaceGaps(cells, counts, cols, gridRows, fallback);
  let min = Infinity;
  let max = -Infinity;
  for (let idx = 0; idx < cells.length; idx += 1) {
    const value = cells[idx]!;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  for (const point of points) {
    if (point.value < min) min = point.value;
    if (point.value > max) max = point.value;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max) || Math.abs(max - min) < 1e-12) {
    min = fallback - 0.5;
    max = fallback + 0.5;
  }
  const denom = Math.sqrt(Math.max(1e-12, directPairs * sxx - sx * sx) * Math.max(1e-12, directPairs * syy - sy * sy));
  const pearsonR = directPairs > 1 ? (directPairs * sxy - sx * sy) / denom : Number.NaN;
  return { cells, counts, cols, rows: gridRows, min, max, points, sampleCount, observedCells, directPairs, pearsonR };
}

function clampView(view: SurfaceViewState): SurfaceViewState {
  return {
    yaw: clampNumber(view.yaw, -Math.PI, Math.PI),
    pitch: clampNumber(view.pitch, -0.9, 1.42),
    zoom: clampNumber(view.zoom, 0.48, 3.2),
    panX: clampNumber(view.panX, -80, 80),
    panY: clampNumber(view.panY, -68, 68),
  };
}

function normValue(value: number, min: number, max: number): number {
  return clampNumber((value - min) / Math.max(1e-12, max - min), 0, 1);
}

function zForValue(value: number, grid: SurfaceGrid): number {
  return (normValue(value, grid.min, grid.max) - 0.5) * LAYOUT.lift;
}

function makeSurfaceMesh(THREE: ThreeModule, grid: SurfaceGrid): InstanceType<ThreeModule["Mesh"]> {
  const positions: number[] = [];
  const colors: number[] = [];
  const color = new THREE.Color();
  for (let row = 0; row < grid.rows; row += 1) {
    for (let col = 0; col < grid.cols; col += 1) {
      const value = grid.cells[row * grid.cols + col]!;
      positions.push(
        -LAYOUT.width / 2 + (col / Math.max(1, grid.cols - 1)) * LAYOUT.width,
        -LAYOUT.depth / 2 + (row / Math.max(1, grid.rows - 1)) * LAYOUT.depth,
        zForValue(value, grid),
      );
      color.setStyle(valueColor(normValue(value, grid.min, grid.max)));
      colors.push(color.r, color.g, color.b);
    }
  }
  const indices: number[] = [];
  for (let row = 0; row < grid.rows - 1; row += 1) {
    for (let col = 0; col < grid.cols - 1; col += 1) {
      const a = row * grid.cols + col;
      const b = a + 1;
      const c = a + grid.cols;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return new THREE.Mesh(
    geometry,
    new THREE.MeshStandardMaterial({ vertexColors: true, side: THREE.DoubleSide, roughness: 0.68, metalness: 0.02 }),
  );
}

function addPointInstances(THREE: ThreeModule, group: InstanceType<ThreeModule["Group"]>, grid: SurfaceGrid): void {
  if (!grid.points.length) return;
  const geometry = new THREE.SphereGeometry(0.035, 10, 6);
  const material = new THREE.MeshBasicMaterial({ color: "#e2e8f0", transparent: true, opacity: 0.86, depthTest: true });
  const mesh = new THREE.InstancedMesh(geometry, material, grid.points.length);
  const dummy = new THREE.Object3D();
  for (let idx = 0; idx < grid.points.length; idx += 1) {
    const point = grid.points[idx]!;
    dummy.position.set(
      -LAYOUT.width / 2 + point.x * LAYOUT.width,
      -LAYOUT.depth / 2 + point.y * LAYOUT.depth,
      zForValue(point.value, grid) + 0.052,
    );
    dummy.updateMatrix();
    mesh.setMatrixAt(idx, dummy.matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  group.add(mesh);
}

function createPanelHandle(THREE: ThreeModule, panel: SurfacePanel): ThreePanelHandle {
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(42, 1, 0.05, 80);
  camera.up.set(0, 0, 1);
  camera.position.set(0, -3.72, 2.42);
  camera.lookAt(0, 0, 0.1);
  scene.add(new THREE.HemisphereLight(0xe5f4ff, 0x0f172a, 1.16));
  const keyLight = new THREE.DirectionalLight(0xffffff, 1.56);
  keyLight.position.set(-2.4, -3.2, 4.2);
  scene.add(keyLight);
  const group = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.PlaneGeometry(LAYOUT.width * 1.05, LAYOUT.depth * 1.18),
    new THREE.MeshBasicMaterial({ color: 0x0f172a, transparent: true, opacity: 0.56, side: THREE.DoubleSide }),
  );
  base.position.z = -0.04;
  group.add(base);
  group.add(makeSurfaceMesh(THREE, panel.grid));
  addPointInstances(THREE, group, panel.grid);
  scene.add(group);
  return { ...panel, scene, camera, group };
}

function applyView(group: InstanceType<ThreeModule["Group"]>, view: SurfaceViewState): void {
  group.rotation.set(clampNumber(view.pitch - 0.34, -1.18, 1.18), 0, view.yaw);
  group.position.set(view.panX * 0.007, -view.panY * 0.007, 0);
  group.scale.setScalar(clampNumber(view.zoom * 0.88, 0.42, 3.2));
}

function disposeScene(scene: InstanceType<ThreeModule["Scene"]>): void {
  scene.traverse((object) => {
    const disposable = object as {
      geometry?: { dispose: () => void };
      material?: { dispose: () => void } | Array<{ dispose: () => void }>;
    };
    disposable.geometry?.dispose();
    if (Array.isArray(disposable.material)) disposable.material.forEach((material) => material.dispose());
    else disposable.material?.dispose();
  });
  scene.clear();
}

function disposeRuntime(runtime: ThreeRuntime): void {
  runtime.observer.disconnect();
  runtime.handles.forEach((handle) => disposeScene(handle.scene));
  runtime.renderer.dispose();
  runtime.renderer.domElement.remove();
}

function SurfaceThreeLayer({
  panels,
  cellRefs,
  view,
  scrollRef,
}: {
  panels: SurfacePanel[];
  cellRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  view: SurfaceViewState;
  scrollRef: MutableRefObject<HTMLDivElement | null>;
}) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const runtimeRef = useRef<ThreeRuntime | null>(null);
  const viewRef = useRef(view);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);

  useEffect(() => {
    viewRef.current = view;
    runtimeRef.current?.render();
  }, [view]);

  useEffect(() => {
    let disposed = false;
    let scrollElement: HTMLDivElement | null = null;
    let scrollRender: (() => void) | null = null;
    const boot = async () => {
      try {
        const THREE = await import("three");
        if (disposed || !rootRef.current) return;
        if (runtimeRef.current) disposeRuntime(runtimeRef.current);
        const root = rootRef.current;
        root.querySelectorAll("canvas").forEach((canvas) => canvas.remove());
        const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: "high-performance" });
        renderer.setClearColor(0x000000, 0);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
        renderer.setScissorTest(true);
        renderer.domElement.style.position = "absolute";
        renderer.domElement.style.inset = "0";
        renderer.domElement.style.width = "100%";
        renderer.domElement.style.height = "100%";
        renderer.domElement.style.pointerEvents = "none";
        root.appendChild(renderer.domElement);
        const handles = panels.map((panel) => createPanelHandle(THREE, panel));
        const render = () => {
          if (disposed || !rootRef.current) return;
          const rootRect = rootRef.current.getBoundingClientRect();
          const width = Math.max(1, Math.floor(rootRect.width));
          const height = Math.max(1, Math.floor(rootRect.height));
          renderer.setSize(width, height, false);
          renderer.setScissorTest(false);
          renderer.setViewport(0, 0, width, height);
          renderer.clear(true, true, true);
          renderer.setScissorTest(true);
          for (const handle of handles) {
            const cell = cellRefs.current[handle.key];
            if (!cell) continue;
            const rect = cell.getBoundingClientRect();
            if (rect.width < 4 || rect.height < 4 || rect.bottom < rootRect.top || rect.top > rootRect.bottom || rect.right < rootRect.left || rect.left > rootRect.right) continue;
            const vx = Math.max(0, Math.floor(rect.left - rootRect.left));
            const vy = Math.max(0, Math.floor(height - (rect.bottom - rootRect.top)));
            const vw = Math.max(1, Math.floor(Math.min(rect.right, rootRect.right) - Math.max(rect.left, rootRect.left)));
            const vh = Math.max(1, Math.floor(Math.min(rect.bottom, rootRect.bottom) - Math.max(rect.top, rootRect.top)));
            renderer.setViewport(vx, vy, vw, vh);
            renderer.setScissor(vx, vy, vw, vh);
            handle.camera.aspect = vw / vh;
            handle.camera.updateProjectionMatrix();
            applyView(handle.group, viewRef.current);
            renderer.render(handle.scene, handle.camera);
          }
        };
        const observer = new ResizeObserver(render);
        observer.observe(root);
        scrollElement = scrollRef.current;
        scrollRender = render;
        scrollElement?.addEventListener("scroll", render, { passive: true });
        runtimeRef.current = { renderer, handles, observer, render };
        setRuntimeError(null);
        render();
      } catch (error) {
        if (!disposed) setRuntimeError(error instanceof Error ? error.message : "3D renderer failed to initialize");
      }
    };
    void boot();
    return () => {
      disposed = true;
      if (scrollElement && scrollRender) scrollElement.removeEventListener("scroll", scrollRender);
      if (runtimeRef.current) {
        disposeRuntime(runtimeRef.current);
        runtimeRef.current = null;
      }
    };
  }, [cellRefs, panels, scrollRef]);

  return (
    <div ref={rootRef} style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }}>
      {runtimeError ? (
        <div style={{ position: "absolute", left: 10, bottom: 10, color: "#fda4af", fontSize: 10, background: "rgba(2,6,23,0.86)", border: "1px solid rgba(248,113,113,0.35)", borderRadius: 7, padding: "5px 7px" }}>
          {runtimeError}
        </div>
      ) : null}
    </div>
  );
}

function DiagonalTile({ series, size }: { series: NumericSeries; size: number }) {
  const bins = useMemo(() => {
    const counts = new Uint32Array(18);
    for (let idx = 0; idx < series.values.length; idx += 1) {
      if (!series.valid[idx]) continue;
      const bucket = clampNumber(Math.floor(normalise(series.values[idx]!, series) * counts.length), 0, counts.length - 1);
      counts[bucket] = (counts[bucket] ?? 0) + 1;
    }
    return counts;
  }, [series]);
  const maxCount = Math.max(1, ...Array.from(bins));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ display: "block", background: "rgba(99,102,241,0.04)" }}>
      {Array.from(bins).map((count, idx) => {
        const w = size / bins.length;
        const h = (count / maxCount) * (size - 18);
        return <rect key={idx} x={idx * w + 1} y={size - h - 8} width={Math.max(1, w - 2)} height={h} rx={2} fill="rgba(99,102,241,0.55)" />;
      })}
      <text x={6} y={12} fill="var(--gray-10, #7b8190)" fontSize="9" fontWeight="700">{series.count.toLocaleString()} pts</text>
    </svg>
  );
}

const SurfaceTile = memo(function SurfaceTile({
  panelKey,
  xColumn,
  yColumn,
  zLabel,
  size,
  buildGrid,
  onPanelReady,
  onOpen,
  cellRefs,
  view,
  onViewChange,
}: {
  panelKey: string;
  xColumn: SurfaceCorrelogramColumn;
  yColumn: SurfaceCorrelogramColumn;
  zLabel: string;
  size: number;
  buildGrid: (xKey: string, yKey: string) => SurfaceGrid | null;
  onPanelReady: (panel: SurfacePanel) => void;
  onOpen?: (panel: SurfacePanel) => void;
  cellRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
  view: SurfaceViewState;
  onViewChange: (view: SurfaceViewState) => void;
}) {
  const [grid, setGrid] = useState<SurfaceGrid | null>(null);
  const [visible, setVisible] = useState(false);
  const dragRef = useRef<{ x: number; y: number; view: SurfaceViewState; mode: "rotate" | "pan"; button: number; moved: boolean } | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const nodeRef = useCallback((node: HTMLDivElement | null) => {
    cellRefs.current[panelKey] = node;
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    const observer = new IntersectionObserver((entries) => setVisible(entries.some((entry) => entry.isIntersecting)), { rootMargin: "260px" });
    observer.observe(node);
    observerRef.current = observer;
  }, [cellRefs, panelKey]);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  useEffect(() => {
    setGrid(null);
  }, [buildGrid, panelKey]);

  useEffect(() => {
    if (!visible || grid) return;
    let cancelled = false;
    const run = () => {
      const next = buildGrid(xColumn.key, yColumn.key);
      if (cancelled || !next) return;
      setGrid(next);
      onPanelReady({ key: panelKey, grid: next, xLabel: xColumn.header, yLabel: yColumn.header, zLabel });
    };
    const requestIdle = window.requestIdleCallback ?? ((callback: IdleRequestCallback) => window.setTimeout(() => callback({ didTimeout: false, timeRemaining: () => 0 }), 1));
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout;
    const idleId = requestIdle(run, { timeout: 180 });
    return () => {
      cancelled = true;
      cancelIdle(idleId);
    };
  }, [buildGrid, grid, onPanelReady, panelKey, visible, xColumn, yColumn, zLabel]);

  const updateView = (nextView: SurfaceViewState) => onViewChange(clampView(nextView));
  const openCurrentPanel = useCallback(() => {
    const nextGrid = grid ?? buildGrid(xColumn.key, yColumn.key);
    if (!nextGrid) return;
    const panel = { key: panelKey, grid: nextGrid, xLabel: xColumn.header, yLabel: yColumn.header, zLabel };
    if (!grid) {
      setGrid(nextGrid);
      onPanelReady(panel);
    }
    onOpen?.(panel);
  }, [buildGrid, grid, onOpen, onPanelReady, panelKey, xColumn.header, xColumn.key, yColumn.header, yColumn.key, zLabel]);

  return (
    <div
      ref={nodeRef}
      role="button"
      tabIndex={0}
      aria-label={`Inspect ${zLabel} surface over ${xColumn.header} and ${yColumn.header}`}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = { x: event.clientX, y: event.clientY, view, mode: event.shiftKey || event.button === 2 ? "pan" : "rotate", button: event.button, moved: false };
      }}
      onPointerMove={(event) => {
        const drag = dragRef.current;
        if (!drag) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
        updateView(drag.mode === "pan"
          ? { ...drag.view, panX: drag.view.panX + dx * 0.62, panY: drag.view.panY + dy * 0.62 }
          : { ...drag.view, yaw: drag.view.yaw + dx * 0.009, pitch: drag.view.pitch + dy * 0.008 });
      }}
      onPointerUp={(event) => {
        const drag = dragRef.current;
        dragRef.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        if (drag && drag.button === 0 && !drag.moved) openCurrentPanel();
      }}
      onPointerCancel={() => { dragRef.current = null; }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        openCurrentPanel();
      }}
      onWheel={(event) => {
        event.preventDefault();
        updateView({ ...view, zoom: view.zoom * Math.exp(-event.deltaY * 0.0012) });
      }}
      onContextMenu={(event) => event.preventDefault()}
      style={{
        width: size,
        height: size,
        position: "relative",
        overflow: "hidden",
        border: "1px solid var(--gray-a5, rgba(148,163,184,0.22))",
        borderRadius: 7,
        background: grid ? "transparent" : "radial-gradient(circle at 50% 42%, rgba(14,165,233,0.12), rgba(2,6,23,0.76) 65%)",
        cursor: "grab",
        userSelect: "none",
        touchAction: "none",
      }}
      title={`${zLabel} over ${xColumn.header} and ${yColumn.header}${grid ? `; ${grid.sampleCount.toLocaleString()} rows, ${grid.observedCells}/${grid.cols * grid.rows} observed bins` : ""}. Click to inspect full size; drag to rotate all 3D cells; wheel to zoom.`}
    >
      <div style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}>
        {!grid ? (
          <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--gray-9)", fontSize: 10 }}>binning</div>
        ) : null}
        <div style={{ position: "absolute", left: 6, top: 5, right: 6, display: "flex", justifyContent: "space-between", gap: 8, color: "var(--gray-12)", textShadow: "0 1px 6px rgba(0,0,0,0.72)", fontSize: 9, fontWeight: 850 }}>
          <span>{zLabel}</span>
          <span style={{ color: "var(--gray-9)", textAlign: "right" }}>{formatSmall(grid?.pearsonR ?? Number.NaN)} r</span>
        </div>
        <div style={{ position: "absolute", left: 6, bottom: 5, right: 6, display: "flex", justifyContent: "space-between", color: "var(--gray-9)", textShadow: "0 1px 6px rgba(0,0,0,0.72)", fontSize: 9 }}>
          <span>{grid ? formatSmall(grid.min) : ""}</span>
          <span>{grid ? `${grid.sampleCount.toLocaleString()} pts` : ""}</span>
          <span>{grid ? formatSmall(grid.max) : ""}</span>
        </div>
      </div>
    </div>
  );
});

function SurfaceMetric({ label, value }: { label: string; value: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", gap: 4, padding: "2px 7px", border: "1px solid var(--gray-a5)", borderRadius: 5, background: "var(--gray-a2)", color: "var(--gray-10)", fontSize: 11 }}>
      <span>{label}</span>
      <strong style={{ color: "var(--gray-12)", fontWeight: 800 }}>{value}</strong>
    </span>
  );
}

function ExpandedSurfaceOverlay({
  panel,
  initialView,
  onClose,
}: {
  panel: SurfacePanel;
  initialView: SurfaceViewState;
  onClose: () => void;
}) {
  const [view, setView] = useState<SurfaceViewState>(() => initialView);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const cellRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dragRef = useRef<{ x: number; y: number; view: SurfaceViewState; mode: "rotate" | "pan"; moved: boolean } | null>(null);
  const panels = useMemo(() => [panel], [panel]);

  useEffect(() => {
    setView(initialView);
  }, [initialView, panel.key]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const updateView = (nextView: SurfaceViewState) => setView(clampView(nextView));
  const observedTotal = panel.grid.cols * panel.grid.rows;
  const content = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${panel.zLabel} surface over ${panel.xLabel} and ${panel.yLabel}`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(2, 6, 23, 0.74)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(1120px, calc(100vw - 48px))",
          height: "min(820px, calc(100vh - 48px))",
          minHeight: 420,
          display: "grid",
          gridTemplateRows: "auto minmax(0, 1fr) auto",
          gap: 10,
          background: "var(--color-surface, #111827)",
          border: "1px solid var(--gray-a5)",
          borderRadius: 9,
          boxShadow: "0 22px 80px rgba(0,0,0,0.54)",
          padding: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "var(--gray-12)", fontSize: 14, fontWeight: 850, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {panel.zLabel} over {panel.xLabel} x {panel.yLabel}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
              <SurfaceMetric label="rows" value={panel.grid.sampleCount.toLocaleString()} />
              <SurfaceMetric label="bins" value={`${panel.grid.observedCells}/${observedTotal}`} />
              <SurfaceMetric label="x-y r" value={formatSmall(panel.grid.pearsonR)} />
              <SurfaceMetric label="range" value={`${formatSmall(panel.grid.min)} to ${formatSmall(panel.grid.max)}`} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setView(DEFAULT_VIEW)}
              style={{ border: "1px solid var(--gray-a5)", background: "var(--gray-a2)", color: "var(--gray-11)", borderRadius: 5, padding: "4px 8px", fontSize: 12, cursor: "pointer" }}
            >
              Reset
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close expanded surface"
              style={{ border: "1px solid var(--gray-a5)", background: "var(--gray-a2)", color: "var(--gray-11)", borderRadius: 5, padding: "4px 8px", fontSize: 16, lineHeight: 1, cursor: "pointer" }}
            >
              &times;
            </button>
          </div>
        </div>

        <div ref={scrollRef} style={{ position: "relative", minHeight: 0, overflow: "hidden", border: "1px solid var(--gray-a5)", borderRadius: 8, background: "linear-gradient(180deg, rgba(15,23,42,0.72), rgba(2,6,23,0.36))" }}>
          <div
            ref={(node) => { cellRefs.current[panel.key] = node; }}
            role="application"
            tabIndex={0}
            onPointerDown={(event) => {
              event.preventDefault();
              event.currentTarget.setPointerCapture(event.pointerId);
              dragRef.current = { x: event.clientX, y: event.clientY, view, mode: event.shiftKey || event.button === 2 ? "pan" : "rotate", moved: false };
            }}
            onPointerMove={(event) => {
              const drag = dragRef.current;
              if (!drag) return;
              const dx = event.clientX - drag.x;
              const dy = event.clientY - drag.y;
              if (Math.abs(dx) + Math.abs(dy) > 3) drag.moved = true;
              updateView(drag.mode === "pan"
                ? { ...drag.view, panX: drag.view.panX + dx * 0.62, panY: drag.view.panY + dy * 0.62 }
                : { ...drag.view, yaw: drag.view.yaw + dx * 0.009, pitch: drag.view.pitch + dy * 0.008 });
            }}
            onPointerUp={(event) => {
              dragRef.current = null;
              if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
            }}
            onPointerCancel={() => { dragRef.current = null; }}
            onWheel={(event) => {
              event.preventDefault();
              updateView({ ...view, zoom: view.zoom * Math.exp(-event.deltaY * 0.0012) });
            }}
            onContextMenu={(event) => event.preventDefault()}
            style={{ position: "absolute", inset: 0, zIndex: 1, cursor: "grab", touchAction: "none", outline: "none" }}
          >
            <div style={{ position: "absolute", left: 12, bottom: 10, right: 12, zIndex: 2, display: "flex", justifyContent: "space-between", color: "var(--gray-9)", fontSize: 11, pointerEvents: "none", textShadow: "0 1px 7px rgba(0,0,0,0.84)" }}>
              <span>{panel.xLabel}</span>
              <span>{panel.yLabel}</span>
            </div>
          </div>
          <SurfaceThreeLayer panels={panels} cellRefs={cellRefs} view={view} scrollRef={scrollRef} />
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, color: "var(--gray-9)", fontSize: 11, lineHeight: 1.4 }}>
          <span>Drag to rotate. Shift-drag or right-drag pans. Wheel zooms.</span>
          <span>{panel.grid.observedCells < observedTotal * 0.18 ? "Sparse direct bins; read the surface as interpolated between observed cells." : "Surface colored by observed/interpolated response bins."}</span>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(content, document.body);
}

export function SurfaceCorrelogram({
  rows,
  numericColumns,
  zAxisKey,
  zAxisLabel,
  zValueResolver,
  selectedRowIdx,
  maxColumns = 20,
  gridResolution = 32,
  pointSampleLimit = 96,
}: SurfaceCorrelogramProps): ReactNode {
  const columns = useMemo(
    () => numericColumns.filter((column) => column.key !== zAxisKey).slice(0, maxColumns),
    [maxColumns, numericColumns, zAxisKey],
  );
  const zColumn = useMemo(
    () => zAxisKey ? numericColumns.find((column) => column.key === zAxisKey) ?? { key: zAxisKey, header: zAxisLabel ?? zAxisKey } : undefined,
    [numericColumns, zAxisKey, zAxisLabel],
  );
  const allSeriesColumns = useMemo(() => {
    const byKey = new Map<string, SurfaceCorrelogramColumn>();
    columns.forEach((column) => byKey.set(column.key, column));
    if (zColumn) byKey.set(zColumn.key, zColumn);
    return Array.from(byKey.values());
  }, [columns, zColumn]);
  const seriesByKey = useMemo(() => buildSeries(rows, allSeriesColumns), [allSeriesColumns, rows]);
  const zSeries = zColumn ? seriesByKey.get(zColumn.key) : undefined;
  const gridCacheRef = useRef(new Map<string, SurfaceGrid>());
  const cellRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const panelsRef = useRef(new Map<string, SurfacePanel>());
  const [panelVersion, setPanelVersion] = useState(0);
  const [view, setView] = useState<SurfaceViewState>(DEFAULT_VIEW);
  const [expandedPanel, setExpandedPanel] = useState<SurfacePanel | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hostObserverRef = useRef<ResizeObserver | null>(null);
  const [hostWidth, setHostWidth] = useState(0);
  const [webgpuAvailable, setWebgpuAvailable] = useState(false);
  const n = columns.length;
  const cellSize = useMemo(() => {
    const fallback = n <= 5 ? 138 : n <= 8 ? 116 : 96;
    if (!n || hostWidth <= 0) return fallback;
    const maxSize = n <= 5 ? 220 : n <= 8 ? 190 : n <= 12 ? 170 : 138;
    const availableWidth = Math.max(0, hostWidth - LABEL_COL_W - 18 - n);
    return Math.floor(clampNumber(availableWidth / n, 74, maxSize));
  }, [hostWidth, n]);
  const matrixWidth = Math.max(hostWidth, LABEL_COL_W + n * (cellSize + 1) + 8);

  const hostRef = useCallback((node: HTMLDivElement | null) => {
    hostObserverRef.current?.disconnect();
    hostObserverRef.current = null;
    if (!node) return;
    const update = () => setHostWidth(node.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setHostWidth(entry.contentRect.width);
    });
    observer.observe(node);
    hostObserverRef.current = observer;
  }, []);

  useEffect(() => () => hostObserverRef.current?.disconnect(), []);

  useEffect(() => {
    gridCacheRef.current.clear();
    panelsRef.current.clear();
    setPanelVersion((value) => value + 1);
  }, [rows, allSeriesColumns, zAxisKey, zValueResolver, gridResolution, pointSampleLimit]);

  useEffect(() => {
    setWebgpuAvailable("gpu" in navigator);
  }, []);

  const buildGrid = useCallback((xKey: string, yKey: string) => {
    if (!zSeries) return null;
    const cacheKey = `${xKey}|${yKey}|${zSeries.key}|${zValueResolver ? "resolved" : "raw"}`;
    const cached = gridCacheRef.current.get(cacheKey);
    if (cached) return cached;
    const xSeries = seriesByKey.get(xKey);
    const ySeries = seriesByKey.get(yKey);
    if (!xSeries || !ySeries) return null;
    const grid = buildSurfaceGrid(xSeries, ySeries, zSeries, rows, zValueResolver, gridResolution, pointSampleLimit);
    gridCacheRef.current.set(cacheKey, grid);
    return grid;
  }, [gridResolution, pointSampleLimit, rows, seriesByKey, zSeries, zValueResolver]);

  const onPanelReady = useCallback((panel: SurfacePanel) => {
    panelsRef.current.set(panel.key, panel);
    setPanelVersion((value) => value + 1);
  }, []);

  const panels = useMemo(() => Array.from(panelsRef.current.values()), [panelVersion]);
  const zLabel = zAxisLabel ?? zColumn?.header ?? zAxisKey ?? "Z";

  if (!zAxisKey || !zSeries || zSeries.count === 0) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--gray-9)", fontSize: 13 }}>
        Set a numeric Z axis to render 3D correlations.
      </div>
    );
  }

  if (n < 2) {
    return (
      <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--gray-9)", fontSize: 13 }}>
        Select at least two numeric fields.
      </div>
    );
  }

  return (
    <div style={{ height: "100%", minHeight: 0, display: "grid", gridTemplateRows: "auto minmax(0, 1fr)", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", color: "var(--gray-9)", fontSize: 11, padding: "0 2px" }}>
        <span style={{ color: "var(--gray-12)", fontWeight: 800 }}>{zLabel} response matrix</span>
        <span>{rows.length.toLocaleString()} rows</span>
        <span>{panels.length}/{n * (n - 1)} surfaces hydrated</span>
        <span>{webgpuAvailable ? "WebGPU available, WebGL active" : "WebGL active"}</span>
        <span>click a surface for full view</span>
        {selectedRowIdx !== undefined ? <span>selected row {selectedRowIdx}</span> : null}
        <button
          type="button"
          onClick={() => setView(DEFAULT_VIEW)}
          style={{ marginLeft: "auto", border: "1px solid var(--gray-a5)", background: "var(--gray-a2)", color: "var(--gray-11)", borderRadius: 5, padding: "2px 7px", fontSize: 11, cursor: "pointer" }}
        >
          Reset 3D
        </button>
      </div>
      <div ref={hostRef} style={{ position: "relative", minHeight: 0, overflow: "hidden" }}>
        <div ref={scrollRef} style={{ position: "absolute", inset: 0, overflow: "auto", zIndex: 1 }}>
          <div style={{ position: "relative", width: matrixWidth, minHeight: HEADER_ROW_H + n * (cellSize + 1), paddingBottom: 8 }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: `${LABEL_COL_W}px repeat(${n}, ${cellSize}px)`,
              gridTemplateRows: `${HEADER_ROW_H}px repeat(${n}, ${cellSize}px)`,
              gap: 1,
            }}>
              <div />
              {columns.map((column) => (
                <div key={`h-${column.key}`} style={{ display: "flex", alignItems: "end", justifyContent: "center", color: "var(--gray-10)", fontSize: 9, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={column.key}>
                  {column.header}
                </div>
              ))}
              {columns.map((rowColumn) => (
                <Fragment key={`r-${rowColumn.key}`}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "end", paddingRight: 5, color: "var(--gray-10)", fontSize: 9, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rowColumn.key}>
                    {rowColumn.header}
                  </div>
                  {columns.map((colColumn) => {
                    if (rowColumn.key === colColumn.key) {
                      const series = seriesByKey.get(rowColumn.key);
                      return (
                        <div key={`${rowColumn.key}-${colColumn.key}`} style={{ width: cellSize, height: cellSize, border: "1px solid var(--gray-a4)", borderRadius: 7, overflow: "hidden" }}>
                          {series ? <DiagonalTile series={series} size={cellSize} /> : null}
                        </div>
                      );
                    }
                    const panelKey = `${rowColumn.key}|${colColumn.key}`;
                    return (
                      <SurfaceTile
                        key={panelKey}
                        panelKey={panelKey}
                        xColumn={colColumn}
                        yColumn={rowColumn}
                        zLabel={zLabel}
                        size={cellSize}
                        buildGrid={buildGrid}
                        onPanelReady={onPanelReady}
                        onOpen={setExpandedPanel}
                        cellRefs={cellRefs}
                        view={view}
                        onViewChange={setView}
                      />
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
        <SurfaceThreeLayer panels={panels} cellRefs={cellRefs} view={view} scrollRef={scrollRef} />
      </div>
      {expandedPanel ? (
        <ExpandedSurfaceOverlay
          panel={expandedPanel}
          initialView={view}
          onClose={() => setExpandedPanel(null)}
        />
      ) : null}
    </div>
  );
}

export default SurfaceCorrelogram;
